/**
 * The single definition of "what state is this route in, and what do we call it". Every surface
 * that shows route state asks here, so the same route never reads one way on the map and another
 * on the timeline.
 *
 * Two rules to keep it that way:
 *   1. Every user-facing string for route state comes from LABELS below. Change the word here,
 *      it changes everywhere.
 *   2. This module returns a semantic `Tone`, never a Tailwind class. Colour lives in lib/tone.ts.
 */
import { differenceInMinutes, isAfter } from "date-fns"

import type { Tone, Variance } from "@/lib/tone"
import { VARIANCE_ORDER } from "@/lib/tone"
import type { Route, RouteStats } from "@/types/collections"
import { formatDuration, getDisplayRouteDelayMinutes } from "@/lib/routeDelayDisplay"

/**
 * Minutes of drift before a route is considered off schedule.
 *
 * Compare against this constant, never a literal: a `>= 5` in one place and a `> 5` in another
 * puts a route exactly five minutes late in the On Time card beneath a "Delayed +5m" badge.
 */
export const DELAY_THRESHOLD_MINUTES = 5

/**
 * Drift at which a route stops being "watch it" and becomes "act on it".
 *
 * Was a bare 15 inside getRouteAttentionIssues. Named because the variance scale now keys its
 * at_risk/late boundary off the same number — if the two ever drift apart, the bar and the row
 * badge start disagreeing about the same route, which is the exact class of bug this module
 * exists to prevent.
 */
export const ESCALATION_THRESHOLD_MINUTES = 15

export type RouteStatusKey =
  "completed" | "cancelled" | "not_started" | "late_start" | "late" | "ahead" | "on_time"

/**
 * Every word the UI uses for route state, in one table.
 *
 * `Late` vs `Delayed` is an open decision — changing it here is the whole change.
 */
const LABELS: Record<RouteStatusKey, string> = {
  completed: "Completed",
  cancelled: "Cancelled",
  not_started: "Not started",
  late_start: "Late start",
  late: "Late",
  ahead: "Ahead",
  on_time: "On time",
}

const TONES: Record<RouteStatusKey, Tone> = {
  completed: "neutral",
  cancelled: "muted",
  not_started: "info",
  late_start: "danger",
  late: "danger",
  ahead: "success",
  on_time: "success",
}

/** The shape of a route this module needs. Deliberately narrow so callers can pass partials. */
export interface RouteForStatus {
  status?: string
  delayMinutes?: number
  plannedStartTime?: Date
  actualStartTime?: Date
  completedStops?: number
  driverName?: string
  vehicleReg?: string
  stops?: Array<{
    plannedArrival: Date
    status?: string
    actualArrival?: Date
    actualDeparture?: Date
    delayMinutes?: number
  }>
}

/**
 * Has the route actually started, as opposed to the DB saying so?
 *
 * The Samsara sync marks a route in_progress as soon as it sees an actualRouteStartTime, and
 * then delayed if the drift exceeds five minutes — so a stray GPS ping can make a route that
 * nobody has driven look like a late one. A recorded start time is the reliable signal.
 *
 * It is not, however, the only one. `actualStartTime` has exactly one writer, the Samsara sync,
 * so a route whose depot geofence never fired keeps a null there for the rest of the day — while
 * its stops fill in from Track-POD underneath. That combination produced a route reading "Late
 * start +12h 3m", a counter climbing since dawn, above a list of collections the driver had
 * already signed for: the panel disbelieving its own contents.
 *
 * A served stop settles the question on its own. Whatever the depot column says, a vehicle that
 * arrived somewhere has set off, so the stops are consulted as a fallback. Note this asks only
 * the boolean — no start time is fabricated from a stop, because the first stop's arrival is an
 * arrival at the first stop, not a departure from the depot.
 *
 * The stray-ping concern above survives intact: this reads resolved stop arrivals and statuses,
 * which are evidence about a specific stop, not the route-level status field that a ping moves.
 */
export function hasGenuinelyStarted(route?: RouteForStatus): boolean {
  if (!route) return true // no context — trust the DB status
  if (route.actualStartTime) return true
  return !!route.stops?.some((s) => s.actualArrival || s.status === "completed")
}

/**
 * Whether a route may still be cancelled, and why not when it may not.
 *
 * Only an already-cancelled route is refused. A started route may be cancelled: a route
 * abandoned mid-shift (breakdown, driver unwell, a site that turned the vehicle away) is real, and
 * cancelling is how dispatch records that it was pulled and closes the day off. The stops already
 * served keep their times and PODs either way.
 *
 * What a started route cannot do is disappear from Track-POD — see `canWithdrawFromTrackpod` —
 * so cancelling one is a record in this tool, not a withdrawal from the driver's app.
 *
 * Everything else is deliberately fair game, including locked routes and past days that never
 * ran: locking freezes dispatch edits rather than the route's existence, and a route nobody
 * drove is precisely the one somebody needs to close off after the fact.
 *
 * Lives here rather than beside the button because three call sites ask the question — the
 * panel's action, the confirm dialog behind it, and the timeline row's icon — and they must
 * agree.
 */
export function canCancelRoute(route?: RouteForStatus & { status?: string }): {
  allowed: boolean
  reason: string | null
} {
  if (!route) return { allowed: false, reason: "No route selected" }
  if (route.status === "cancelled") {
    return { allowed: false, reason: "This route is already cancelled" }
  }
  return { allowed: true, reason: null }
}

/**
 * Can cancelling this route also pull it out of Track-POD?
 *
 * Only before it sets off. Track-POD will not delete a route the driver is working, and the
 * orders under it hold the PODs for stops already served — deleting them is neither possible nor
 * wanted. The backend applies the same rule when it is asked to withdraw a route; this is here so
 * the dialog can say which of the two cancellations the operator is about to perform.
 */
export function canWithdrawFromTrackpod(route?: RouteForStatus): boolean {
  return !hasGenuinelyStarted(route)
}

/**
 * The time a route is judged late against.
 *
 * The first stop's planned arrival, not the route-level planned start: the latter is usually a
 * depot departure that can be hours earlier and says nothing about whether the customer is
 * being kept waiting.
 */
export function getRouteDueTime(route?: RouteForStatus): Date | null {
  if (!route) return null
  if (route.stops && route.stops.length > 0) return route.stops[0].plannedArrival
  return route.plannedStartTime || null
}

/**
 * Collapse the DB status onto the four states that actually partition a day's routes.
 * Used by the stats band so its segments sum to the total by construction.
 */
export type EffectiveStatus = "not_started" | "in_progress" | "completed" | "cancelled"

export function getEffectiveStatus(route?: RouteForStatus): EffectiveStatus {
  const dbStatus = route?.status
  if (dbStatus === "completed") return "completed"
  if (dbStatus === "cancelled") return "cancelled"
  if (!hasGenuinelyStarted(route)) return "not_started"
  return "in_progress"
}

export interface RouteStatusDisplay {
  key: RouteStatusKey
  /** Bare state, e.g. "Late". */
  label: string
  /** State with its magnitude, e.g. "Late +25m". Equals `label` when there's nothing to add. */
  detailLabel: string
  tone: Tone
  /** Signed drift in minutes; positive is late. Zero for states where drift is meaningless. */
  delayMinutes: number
}

/**
 * Resolve a route to exactly one display state.
 *
 * `now` is injectable so this is testable and so a view rendering a past date can pass the end
 * of that day rather than the wall clock.
 */
export function getRouteStatusDisplay(
  route: RouteForStatus,
  now: Date = new Date()
): RouteStatusDisplay {
  const effective = getEffectiveStatus(route)

  if (effective === "completed") {
    return {
      key: "completed",
      label: LABELS.completed,
      detailLabel: LABELS.completed,
      tone: TONES.completed,
      delayMinutes: 0,
    }
  }

  if (effective === "cancelled") {
    return {
      key: "cancelled",
      label: LABELS.cancelled,
      detailLabel: LABELS.cancelled,
      tone: TONES.cancelled,
      delayMinutes: 0,
    }
  }

  if (effective === "not_started") {
    const dueTime = getRouteDueTime(route)
    if (dueTime && isAfter(now, dueTime)) {
      const lateMinutes = differenceInMinutes(now, dueTime)
      return {
        key: "late_start",
        label: LABELS.late_start,
        detailLabel: `${LABELS.late_start} +${formatDuration(lateMinutes, true)}`,
        tone: TONES.late_start,
        delayMinutes: lateMinutes,
      }
    }
    return {
      key: "not_started",
      label: LABELS.not_started,
      detailLabel: LABELS.not_started,
      tone: TONES.not_started,
      delayMinutes: 0,
    }
  }

  const delay = getDisplayRouteDelayMinutes({
    delayMinutes: route.delayMinutes ?? 0,
    stops: route.stops,
  })

  if (delay >= DELAY_THRESHOLD_MINUTES) {
    return {
      key: "late",
      label: LABELS.late,
      detailLabel: `${LABELS.late} +${formatDuration(delay, true)}`,
      tone: TONES.late,
      delayMinutes: delay,
    }
  }

  if (delay <= -DELAY_THRESHOLD_MINUTES) {
    return {
      key: "ahead",
      label: LABELS.ahead,
      detailLabel: `${LABELS.ahead} ${formatDuration(Math.abs(delay), true)}`,
      tone: TONES.ahead,
      delayMinutes: delay,
    }
  }

  return {
    key: "on_time",
    label: LABELS.on_time,
    detailLabel: LABELS.on_time,
    tone: TONES.on_time,
    delayMinutes: delay,
  }
}

/** Tone for a stored route-instance status, for views that show the raw status word. */
export function getRouteInstanceStatusTone(status: string): Tone {
  switch (status) {
    case "completed":
      return TONES.completed
    case "cancelled":
      return TONES.cancelled
    case "delayed":
      return TONES.late
    case "ahead":
      return TONES.ahead
    case "in_progress":
      return TONES.on_time
    default:
      return TONES.not_started
  }
}

/** A planned route's lifecycle state, as a label and a tone. */
export function getPlannedRouteStatusDisplay(status: string): { tone: Tone; label: string } {
  if (status === "live") return { tone: "success", label: "Live" }
  if (status === "draft") return { tone: "warning", label: "Draft" }
  return { tone: TONES.cancelled, label: LABELS.cancelled }
}

// ---------------------------------------------------------------------------
// Attention
// ---------------------------------------------------------------------------

export type AttentionIssueKind =
  "late_start" | "no_driver" | "no_vehicle" | "running_late" | "over_capacity"

export interface AttentionIssue {
  kind: AttentionIssueKind
  label: string
  tone: Extract<Tone, "danger" | "warning">
}

/**
 * Why a route is in danger, in one vocabulary.
 *
 * Two families, deliberately worded so they never collide with the band names:
 *
 *   missing a resource — "no driver", "no vehicle". Fixable by assignment.
 *   everything else    — "over capacity" (loaded wrong), "not set off" and "delayed" (schedule).
 *
 * These read alongside the bar, which already says *how bad* (at risk / late / not started), so
 * these must say *what is wrong* instead. A cause worded as how bad ("late start", "running
 * late") gives two vocabularies for one idea beneath the bar, with no way to reconcile the
 * numbers.
 *
 * There is no "no trailer": the route model carries driver and vehicle registration only, so
 * that category needs a data field before it can mean anything.
 */
export const ATTENTION_ISSUE_LABELS: Record<AttentionIssueKind, string> = {
  no_driver: "no driver",
  no_vehicle: "no vehicle",
  over_capacity: "over capacity",
  late_start: "not departed",
  running_late: "delayed",
}

/**
 * Which issue a route is counted under when it has several.
 *
 * Resources first, schedule last, because that is the order of what you can *do*. A route with
 * no driver is also, inevitably, about to be late — counting it as "delayed" describes the
 * symptom and hides the one thing anybody can act on. Same cause-before-effect rule the
 * intervention text follows.
 */
export const CAUSE_PRIORITY: AttentionIssueKind[] = [
  "no_driver",
  "no_vehicle",
  "over_capacity",
  "late_start",
  "running_late",
]

/**
 * Minutes before a route is due at which an unassigned driver or vehicle becomes a problem.
 *
 * Exported because the timeline row needs the same window to decide whether "no driver" earns
 * alarm colour. Red regardless would have a night route with nothing booked shouting from first
 * light — and a standing red that means nothing is how operators learn to stop reading red.
 */
export const UNASSIGNED_WARNING_WINDOW_MINUTES = 60

/**
 * "Unassigned" is spelled three different ways in the data — missing, empty, and the literal
 * string "Unassigned" that the planner writes. One definition, so the variance scale, the issue
 * list and the concern score can't disagree about whether a route has a driver.
 */
function hasNoDriver(route: RouteForStatus): boolean {
  return !route.driverName || route.driverName.trim() === "" || route.driverName === "Unassigned"
}

function hasNoVehicle(route: RouteForStatus): boolean {
  return !route.vehicleReg || route.vehicleReg.trim() === ""
}

function isUnassigned(route: RouteForStatus): boolean {
  return hasNoDriver(route) || hasNoVehicle(route)
}

/**
 * What is wrong with this route right now, if anything.
 *
 * Deliberately narrower than "is it late": a route scheduled for this afternoon that hasn't
 * started is entirely normal and must not be flagged. The rules are
 *   1. should have started and hasn't
 *   2. no driver or no vehicle, and due within the hour (or already overdue)
 *   3. genuinely running and drifting past the threshold
 *   4. loaded beyond capacity
 */
export function getRouteAttentionIssues(
  route: RouteForStatus,
  now: Date = new Date(),
  loadFactor?: number
): AttentionIssue[] {
  const effective = getEffectiveStatus(route)
  if (effective === "completed" || effective === "cancelled") return []

  const issues: AttentionIssue[] = []
  const genuinelyStarted = hasGenuinelyStarted(route)
  const dueTime = getRouteDueTime(route)
  const isPastDue = dueTime ? isAfter(now, dueTime) : false
  const minutesToDue = dueTime ? differenceInMinutes(dueTime, now) : Number.MAX_SAFE_INTEGER
  const dueSoon = isPastDue || minutesToDue <= UNASSIGNED_WARNING_WINDOW_MINUTES

  if (!genuinelyStarted && isPastDue && dueTime) {
    issues.push({
      kind: "late_start",
      label: `${LABELS.late_start} +${formatDuration(differenceInMinutes(now, dueTime), true)}`,
      tone: "danger",
    })
  }

  if (hasNoDriver(route) && dueSoon) {
    issues.push({ kind: "no_driver", label: "No driver", tone: isPastDue ? "danger" : "warning" })
  }

  if (hasNoVehicle(route) && dueSoon) {
    issues.push({ kind: "no_vehicle", label: "No vehicle", tone: isPastDue ? "danger" : "warning" })
  }

  if (genuinelyStarted) {
    const delay = getDisplayRouteDelayMinutes({
      delayMinutes: route.delayMinutes ?? 0,
      stops: route.stops,
    })
    if (delay >= ESCALATION_THRESHOLD_MINUTES) {
      issues.push({
        kind: "running_late",
        label: `${LABELS.late} +${formatDuration(delay, true)}`,
        tone: "danger",
      })
    } else if (delay >= DELAY_THRESHOLD_MINUTES) {
      issues.push({
        kind: "running_late",
        label: `${LABELS.late} +${formatDuration(delay, true)}`,
        tone: "warning",
      })
    }
  }

  if (loadFactor !== undefined && loadFactor >= 100) {
    issues.push({ kind: "over_capacity", label: `${loadFactor.toFixed(0)}% full`, tone: "danger" })
  }

  return issues
}

/**
 * Weighted urgency, for sorting "most concerning first".
 *
 * Kept as a score rather than a boolean because the timeline sorts by it; membership of the
 * attention list is `score >= ATTENTION_THRESHOLD`.
 */
export function getRouteConcernScore(
  route: RouteForStatus,
  now: Date = new Date(),
  loadFactor?: number
): number {
  const effective = getEffectiveStatus(route)
  if (effective === "completed" || effective === "cancelled") return 0

  let score = 0
  const genuinelyStarted = hasGenuinelyStarted(route)
  const dueTime = getRouteDueTime(route)
  const isPastDue = dueTime ? isAfter(now, dueTime) : false
  const minutesToDue = dueTime ? differenceInMinutes(dueTime, now) : Number.MAX_SAFE_INTEGER

  if (!genuinelyStarted && isPastDue && dueTime) {
    score += Math.min(100, 50 + differenceInMinutes(now, dueTime))
  }

  if (isUnassigned(route) && (isPastDue || minutesToDue <= UNASSIGNED_WARNING_WINDOW_MINUTES)) {
    score += isPastDue ? 80 : 40
  }

  if (genuinelyStarted) {
    const delay = getDisplayRouteDelayMinutes({
      delayMinutes: route.delayMinutes ?? 0,
      stops: route.stops,
    })
    if (delay >= 30) score += 60
    else if (delay >= ESCALATION_THRESHOLD_MINUTES) score += 40
    else if (delay >= DELAY_THRESHOLD_MINUTES) score += 20
  }

  if (loadFactor !== undefined && loadFactor >= 100) score += 25

  return score
}

// ---------------------------------------------------------------------------
// Schedule variance
// ---------------------------------------------------------------------------

/**
 * Which band of the variance scale a route sits in.
 *
 * The band this feeds partitions the day by *deviation from plan*, not by run state. Run state
 * (done / running / to start) answers "what are these routes doing", which nobody asks; variance
 * answers "are we on plan and which way are we off it", which is the whole job.
 *
 * The split that matters is inside not-started. A route that hasn't started and isn't due for
 * three hours is the healthiest thing on the board. A route that should have left twenty minutes
 * ago and hasn't is the *worst* state available — worse than one running late, because a late
 * route is at least moving and shedding delay, while this one has a problem nobody has touched.
 * Bucketing the two together lets the band look calm while three routes sit unstarted and
 * overdue.
 *
 * Thresholds reuse the existing constants on purpose, so the bar and the per-row badge can never
 * disagree: at_risk begins at DELAY_THRESHOLD_MINUTES (the same drift that earns a "Late" badge)
 * and late begins at ESCALATION_THRESHOLD_MINUTES (the same drift that turns that badge red).
 *
 * Load factor is deliberately *not* an input. Over-capacity is a real problem but it is not a
 * schedule variance, and folding a second axis into an ordered scale is what breaks ordered
 * scales. It surfaces as an intervention instead — see getRouteIntervention.
 *
 * Returns null for cancelled routes: they are no longer on the plan, so they have no variance
 * from it. The caller counts them separately rather than drawing them on the axis.
 */
/**
 * The words for each band. Same rule as LABELS above: change it here, it changes everywhere.
 *
 * Kept to two words at most because the legend sits inline with the bar on one line, and a long
 * label there wraps the whole band onto a second row — which is the height this design exists to
 * save. `breached` reads "not started" rather than "overdue": the useful distinction from `late`
 * is running-but-behind versus never-set-off, and those two words carry it exactly, where two
 * near-synonyms would not. That it is *overdue* comes from its position on the scale and from
 * the intervention row, which names the time it should have gone.
 */
export const VARIANCE_LABELS: Record<Variance, string> = {
  done: "done",
  on_plan: "on plan",
  at_risk: "at risk",
  late: "late",
}

export function getRouteVariance(route: RouteForStatus, now: Date = new Date()): Variance | null {
  const effective = getEffectiveStatus(route)
  if (effective === "cancelled") return null
  if (effective === "completed") return "done"

  const dueTime = getRouteDueTime(route)
  const isPastDue = dueTime ? isAfter(now, dueTime) : false

  if (effective === "not_started") {
    // Past its start time and still sitting there *is* running late — the fact that no wheels
    // have turned is the cause, not a different kind of lateness, and it surfaces as
    // "not departed" on the cause line. Not its own band: a second, darker red is one nobody can
    // reliably tell from the first; see the note in lib/tone.ts.
    if (isPastDue) return "late"
    // Unassigned and due imminently can no longer start on time, even though it isn't late yet.
    const minutesToDue = dueTime ? differenceInMinutes(dueTime, now) : Number.MAX_SAFE_INTEGER
    if (minutesToDue <= UNASSIGNED_WARNING_WINDOW_MINUTES && isUnassigned(route)) return "at_risk"
    return "on_plan"
  }

  const delay = getDisplayRouteDelayMinutes({
    delayMinutes: route.delayMinutes ?? 0,
    stops: route.stops,
  })
  if (delay >= ESCALATION_THRESHOLD_MINUTES) return "late"
  if (delay >= DELAY_THRESHOLD_MINUTES) return "at_risk"
  return "on_plan"
}

/** Running far enough ahead to be worth saying so. Off the variance axis by design. */
export function isAheadOfPlan(route: RouteForStatus, now: Date = new Date()): boolean {
  return getRouteStatusDisplay(route, now).key === "ahead"
}

export interface VarianceSummary {
  /** Counts per band, in VARIANCE_ORDER. Bands with no routes are still present, at zero. */
  counts: Record<Variance, number>
  /** Routes on the axis — everything except cancelled. The bar's denominator. */
  total: number
  ahead: number
  /**
   * On plan and not yet away: the part of the day still ahead of you.
   *
   * A subset of `on_plan`, not a band of its own — these routes are perfectly healthy, and the
   * moment one goes past its start time it becomes `late`. Reported as a plain number because
   * that is what it is: a fact about how much is still to come, not a problem.
   */
  notStarted: number
  cancelled: number
}

/**
 * `varianceOf` lets a caller substitute a band it has already resolved — in practice the
 * hysteresis-smoothed one from useStableVariance, so the counts match the rows on screen rather
 * than the raw reading a threshold-hugging route happens to give this minute.
 */
export function summariseVariance(
  routes: RouteForStatus[],
  now: Date = new Date(),
  varianceOf: (route: RouteForStatus) => Variance | null = (r) => getRouteVariance(r, now)
): VarianceSummary {
  const counts = Object.fromEntries(VARIANCE_ORDER.map((v) => [v, 0])) as Record<Variance, number>
  let total = 0
  let ahead = 0
  let notStarted = 0
  let cancelled = 0

  for (const route of routes) {
    const variance = varianceOf(route)
    if (variance === null) {
      cancelled++
      continue
    }
    counts[variance]++
    total++
    if (variance === "on_plan") {
      if (isAheadOfPlan(route, now)) ahead++
      if (getEffectiveStatus(route) === "not_started") notStarted++
    }
  }

  return { counts, total, ahead, notStarted, cancelled }
}

/**
 * What each band of the bar is made of, for its hover breakdown.
 *
 * Composition, never identity. The counts answer "how bad" but not "bad how" — eight at risk
 * could be one depot's worth of unassigned vehicles or eight unrelated routes drifting in
 * traffic, and those want completely different responses.
 *
 * No individual routes here. A tooltip is transient and non-interactive, so a list of route codes
 * inside one can only be memorised and then hunted for elsewhere. Identity already has two better
 * homes: the intervention rows, and the "N more" link that filters the list below. An aggregate
 * cause is the one thing you can act on without remembering anything.
 *
 * Each route is counted once, under its primary issue, so the lines sum to the band's count.
 *
 * `done` gets no breakdown on purpose: a completed route has nothing left to say, and we don't
 * retain whether it finished late, so any line would either repeat the count or invent something.
 * A band with no entry simply has no tooltip.
 */
/**
 * The one issue a route is counted under.
 *
 * Shared by the per-band breakdown and the whole-day cause line, so the two can never attribute
 * the same route to different problems.
 */
function primaryCause(
  route: RouteForStatus,
  now: Date,
  loadFactor?: number
): AttentionIssueKind | undefined {
  const kinds = new Set(getRouteAttentionIssues(route, now, loadFactor).map((i) => i.kind))
  return CAUSE_PRIORITY.find((kind) => kinds.has(kind))
}

/** Cause counts as display lines, dominant first — that is the one worth doing something about. */
function causeLines(counts: Map<AttentionIssueKind, number>): string[] {
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([kind, count]) => `${count} ${ATTENTION_ISSUE_LABELS[kind]}`)
}

/**
 * What the whole day's alarm routes are made of, regardless of which band they sit in.
 *
 * Shown permanently in the band rather than on hover, because it is the most useful thing after
 * the count and it costs nothing in trust: it covers every route needing a human, counts each
 * once, and so sums to the headline figure. "Six routes short a vehicle" is a depot problem you
 * can act on without knowing which six.
 */
export function summariseAlarmCauses<T extends RouteForStatus>(
  routes: T[],
  now: Date,
  opts: {
    varianceOf?: (route: T) => Variance | null
    loadFactor?: (route: T) => number | undefined
  } = {}
): string[] {
  const varianceOf = opts.varianceOf ?? ((r: T) => getRouteVariance(r, now))
  const counts = new Map<AttentionIssueKind, number>()

  for (const route of routes) {
    const band = varianceOf(route)
    if (band === null || band === "done" || band === "on_plan") continue
    const kind = primaryCause(route, now, opts.loadFactor?.(route))
    if (kind) counts.set(kind, (counts.get(kind) ?? 0) + 1)
  }

  return causeLines(counts)
}

export function describeVarianceBands<T extends RouteForStatus>(
  routes: T[],
  now: Date,
  opts: {
    varianceOf?: (route: T) => Variance | null
    loadFactor?: (route: T) => number | undefined
  } = {}
): Partial<Record<Variance, string[]>> {
  const varianceOf = opts.varianceOf ?? ((r: T) => getRouteVariance(r, now))
  const causes = new Map<Variance, Map<AttentionIssueKind, number>>()

  let running = 0
  let notDue = 0
  let ahead = 0

  for (const route of routes) {
    const band = varianceOf(route)
    if (band === null || band === "done") continue

    if (band === "on_plan") {
      if (isAheadOfPlan(route, now)) ahead++
      else if (getEffectiveStatus(route) === "in_progress") running++
      else notDue++
      continue
    }

    const primary = primaryCause(route, now, opts.loadFactor?.(route))
    if (!primary) continue
    const perBand = causes.get(band) ?? new Map<AttentionIssueKind, number>()
    perBand.set(primary, (perBand.get(primary) ?? 0) + 1)
    causes.set(band, perBand)
  }

  const out: Partial<Record<Variance, string[]>> = {}
  for (const [band, perBand] of causes) out[band] = causeLines(perBand)

  const onPlan = [
    running && `${running} running to plan`,
    notDue && `${notDue} not due yet`,
    ahead && `${ahead} running ahead`,
  ].filter((line): line is string => !!line)
  if (onPlan.length > 0) out.on_plan = onPlan

  return out
}

// ---------------------------------------------------------------------------
// Per-stop variance
// ---------------------------------------------------------------------------

/**
 * One stop time measured against its plan.
 *
 * Every place that shows a stop's drift -- the CSV export, the row badge, the bar offset, the
 * tooltip and the stop sheet -- takes it from here. Green must mean "on time", not "we have a
 * value", and a stop sheet is exactly where someone goes to find out which.
 *
 * Bands come off the route constants rather than a number picked here, so a stop can never show
 * amber in the sheet while its row badge and the band above both call the route on plan.
 *
 * `band` is the ordered scale for anything that wants to sort or compare; `tone` is what a
 * component should actually render, per the rule at the top of this module — this file returns
 * semantics, lib/tone.ts owns the colour.
 */
export interface StopTimeVariance {
  /** Signed minutes from plan: positive is late, negative early. */
  minutes: number
  /**
   * Never `done` — a band describes a route's progress through its day, while this describes a
   * single moment that has either already drifted or hasn't.
   */
  band: Exclude<Variance, "done">
  tone: Tone
  /** "+18 min", "−6 min", "on time". */
  label: string
}

/**
 * Which band a drift in minutes lands in.
 *
 * Exported separately because a caller that already has a signed figure — a row reading a
 * persisted `delay_minutes`, say — should classify it through the same ladder rather than
 * writing the two comparisons out again.
 */
export function stopVarianceBand(minutes: number): Exclude<Variance, "done"> {
  if (minutes >= ESCALATION_THRESHOLD_MINUTES) return "late"
  if (minutes >= DELAY_THRESHOLD_MINUTES) return "at_risk"
  return "on_plan"
}

/**
 * Early is deliberately not a band of its own.
 *
 * On the route scale `ahead` sits off-axis because a whole route running early is worth
 * remarking on. A single stop reached six minutes early is not: it is normal execution, it
 * carries no action, and giving it a third colour would spend one of the few saturated hues on
 * the screen on non-news. The signed figure still reports it, which is all anyone wanted.
 */
export function getStopTimeVariance(
  planned: Date | null | undefined,
  observed: Date | null | undefined
): StopTimeVariance | null {
  if (!planned || !observed) return null

  const minutes = differenceInMinutes(observed, planned)
  const band = stopVarianceBand(minutes)

  // on_plan maps to success rather than neutral: a time that landed on its plan is a result, not
  // an absence of one, and the sheet should read as reassuring.
  const tone: Tone = band === "late" ? "danger" : band === "at_risk" ? "warning" : "success"

  // Rounded to the minute the clock shows, so the delta always reconciles with the two HH:mm
  // times printed either side of it.
  const label =
    minutes === 0
      ? "on time"
      : minutes > 0
        ? `+${formatDuration(minutes)}`
        : `\u2212${formatDuration(minutes)}`

  return { minutes, band, tone, label }
}

/**
 * A stop's own status, as a label and a tone.
 *
 * Through the same tones as a route, so a completed stop wears the same green as the completed
 * route two hundred pixels above it, and with a real label rather than the raw enum.
 *
 * `arrived` and `processing` share a tone deliberately: to an operator both mean the driver is
 * there and the stop is happening, and only the label needs to separate them.
 */
export function getStopStatusDisplay(status: string): { tone: Tone; label: string } {
  switch (status) {
    case "completed":
      return { tone: "success", label: "Completed" }
    case "processing":
      return { tone: "info", label: "At stop" }
    case "arrived":
      return { tone: "info", label: "Arrived" }
    case "en_route":
      return { tone: "info", label: "En route" }
    case "skipped":
      return { tone: "muted", label: "Skipped" }
    case "scheduled":
      return { tone: "neutral", label: "Scheduled" }
    default:
      // Older rows carry statuses that predate the enum ("pending"). Title-cased rather than
      // dropped: an unrecognised value is still the truth about the stop.
      return {
        tone: "neutral",
        label: status
          ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
          : "Unknown",
      }
  }
}

// ---------------------------------------------------------------------------
// Day summary
// ---------------------------------------------------------------------------

/**
 * Summarise a day's routes for the stats band.
 *
 * Both halves are derived from getRouteStatusDisplay, so the numbers here and the badge on each
 * row cannot disagree.
 */
export const calculateRouteStats = (routes: Route[], now: Date = new Date()): RouteStats => {
  const stats: RouteStats = {
    totalRoutes: routes.length,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    cancelled: 0,
    late: 0,
    ahead: 0,
    onTime: 0,
  }

  for (const route of routes) {
    const { key } = getRouteStatusDisplay(route, now)

    switch (key) {
      case "completed":
        stats.completed++
        break
      case "cancelled":
        stats.cancelled++
        break
      case "not_started":
        stats.notStarted++
        break
      case "late_start":
        // Still hasn't started, so it belongs in the not-started segment — but it is also an
        // exception, which is the whole reason the two groups are counted separately.
        stats.notStarted++
        stats.late++
        break
      case "late":
        stats.inProgress++
        stats.late++
        break
      case "ahead":
        stats.inProgress++
        stats.ahead++
        break
      case "on_time":
        stats.inProgress++
        stats.onTime++
        break
    }
  }

  return stats
}
